import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation, withTranslation } from "react-i18next";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import { get, isEmpty } from "lodash";
import FloatInput from "./FloatInput";
import validator from "validator";
import ReservationService from "../../backend/ReservationService";
import { ClipLoaderIcon } from "../../components/LoadingIcon";
import { convertToAmPm } from "../../utils/timeUtils";

const AddYourselfToTrip = () => {
  const {
    propertyUid,
    bookingNumber,
    language,
    lastName,
    resetBooking,
    userEmail,
    func: { useFormInput, updateGuests, updateErrorPage, setAccessTokenData },
    data: { presets, accessToken, booking, guests, property, configuration, guestService },
    components: { PreCheckInHeader, PreCheckInContainer },
  } = usePreCheckInContext();
  const initialFormState = {
    adultFirstName: "",
    adultLastName: "",
    adultEmail: userEmail,
  };

  const history = useHistory();
  const { t } = useTranslation();
  const [formData, handleFormChange] = useFormInput(initialFormState);
  const [validateForm, setValidateForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const goBack = useCallback(() => {
    history.push(
      `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
        lastName
      )}?language=${language}&token=${accessToken}&email=${encodeURIComponent(
        userEmail
      )}`
    );
  }, [
    propertyUid,
    bookingNumber,
    lastName,
    language,
    accessToken,
    history,
    userEmail,
  ]);

  useEffect(() => {
    if (loading) {
      if (submitted && resetBooking) {
        setSubmitted(false);
      } else if (!submitted && !resetBooking && !isEmpty(booking)) {
        setLoading(false);
        goBack();
      }
    }
  }, [loading, resetBooking, booking, submitted, goBack]);

  const addBtn = () => {
    setSubmitted(true);
    const validate =
      !isEmpty(formData.adultFirstName) &&
      !isEmpty(formData.adultLastName) &&
      !isEmpty(formData.adultEmail) &&
      validator.isEmail(formData.adultEmail);
    if (validate) {
      if (presets.demoMode) {
        keepGuestDataAndMove(prepareData());
      } else {
        requestCreateAdditionalGuest(prepareData());
      }
    }
    setValidateForm(validate);
  };

  const generateDefaultValues = () => {
    const guestId = Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000;
    return {
      guestId,
      id: "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }),
      guest_id: guestId,
      avatar: null,
      account_exist: false,
      license_plate: null,
      pre_checked_in_completed: false,
    };
  };

  const keepGuestDataAndMove = (newChildrenData) => {
    let guestData = {
      ...newChildrenData.guest_details,
      firstName: newChildrenData.guest_details.first_name,
      lastName: newChildrenData.guest_details.last_name,
      ...generateDefaultValues(),
    };
    updateAdult(guestData);
    goBack();
  };

  const updateAdult = (guestData) => {
    if (isEmpty(guestData)) return;

    guests.newGuest({
      ...guestData,
      editable: true,
      currentGuest: true,
    });
    updateGuests(guests);
  };

  const requestCreateAdditionalGuest = async (newChildrenData) => {
    setLoading(true);
    window.scrollTo(0, 0);
    await ReservationService.createAdditionalGuest(
      accessToken,
      propertyUid,
      newChildrenData
    )
      .then((response) => {
        updateAdult(get(response.data, "guest_details"));
        goBack();
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        updateErrorPage(true);
      });
  };

  const prepareData = () => {
    const reservationId = get(booking, "resv_id");
    const data = {
      reservation_id: reservationId,
      property_uid: propertyUid,
      guest_details: {
        first_name: formData.adultFirstName,
        last_name: formData.adultLastName,
        email: formData.adultEmail,
        children: [],
      },
      reservation: {
        property_uid: propertyUid,
        reservation_id: reservationId,
      },
    };
    return data;
  };
  return (
    <>
      <PreCheckInContainer containerName="add-yourself-to-trip">
        <PreCheckInHeader
          title={t("ADD_YOURSELF_TO_THE_TRIP")}
          headerLink
          onBack={() => {
            history.push(
              `/pre-checkin/${propertyUid}/home?language=${language}&token=${accessToken}`
            );
          }}
        />
        {presets && booking && (
          <>
            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
              <div className="flex flex-col h-full">
                <div className="flex mb-10 w-full">
                  <div className="font-normal m-auto">
                    <p
                      className="mb-12 type-normal large text-center justify-center"
                      style={{ color: presets.titleColor }}>
                      {t("NEED_ASSISTANCE_RESERVATION")}
                    </p>

                    <div className="flex flex-col h-full">
                      <div className="flex mb-10 w-full">
                        <div
                          className="mr-8 w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden flex items-center"
                          style={{
                            backgroundColor: presets.backgroundColorTopBar,
                          }}>
                          {property.property_system_icon && (
                            <img
                              className="w-full"
                              src={property.property_system_icon}
                              alt={t("HOTEL_LOGO", "Hotel Logo")}
                            />
                          )}
                        </div>
                        <div className="capitalize font-normal">
                          <h1
                            className="mb-2 type-normal large"
                            style={{ color: presets.titleColor }}>
                            {property.name}
                          </h1>
                          {!guestService.hide_room_type && <p
                            className="mb-2 type-regular medium"
                            style={{ color: presets.subtitleColor }}>
                            {get(booking, "room_description")}
                          </p>}
                          <p
                            className="mb-2 type-regular medium"
                            style={{ color: presets.subtitleColor }}>
                            {t("BOOKING_NUMBER")} {get(booking, "booking_id")}
                          </p>
                          <p
                            className="mt-8 type-regular medium"
                            style={{ color: presets.subtitleColor }}>
                            {configuration.checkPreCheckInInterfaceTypeIsHotelKey
                              ? t("BEDS", "Beds")
                              : t("ROOMS")}
                            :
                            <span className="font-bold pl-1">
                              {get(booking, "number_of_rooms", 1)}
                            </span>{" "}
                            |{" "}
                            {configuration.checkPreCheckInInterfaceTypeIsHotelKey
                              ? t("MAX_GUESTS", "Max Guests")
                              : t("GUESTS")}
                            :
                            <span className="font-bold">
                              {" "}
                              {get(booking, "total_guests", 1)}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="md:max-w-7xl mx-auto w-full mb-4">
                        <div className="capitalize font-normal flex w-full mb-7">
                          <div className="flex flex-1 justify-end">
                            <div className="flex flex-col space-y-1">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-9 h-9 mx-auto mb-4"
                                viewBox="0 0 448 512">
                                <path
                                  fill={`${presets.backgroundColorTopBar}`}
                                  d="M148 288h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm108-12v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 96v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm192 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96-260v352c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V112c0-26.5 21.5-48 48-48h48V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h128V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h48c26.5 0 48 21.5 48 48zm-48 346V160H48v298c0 3.3 2.7 6 6 6h340c3.3 0 6-2.7 6-6z"></path>
                              </svg>
                              <p
                                className="text-okkami-gray text-center type-regular medium uppercase"
                                style={{ color: presets.subtitleColor }}>
                                {t("CHECK_IN")}
                              </p>
                              <p
                                style={{ color: presets.titleColor }}
                                className="text-okkami-dark-gray text-center type-title normal uppercase">
                                {booking.checkInDateText}
                              </p>
                              <p
                                className="text-center type-normal normal"
                                style={{ color: presets.titleColor }}>
                                {`${property.isEvermore ? t("BEGINS_AT", "") : ""} ${presets.amPm
                                ? convertToAmPm(configuration.checkInTimeText)
                                : configuration.checkInTimeText}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-center items-center mx-8 pt-4">
                            <svg
                              className="w-7 h-7 mx-auto"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 448 512">
                              <path
                                fill={`${presets.backgroundColorTopBar}`}
                                d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path>
                            </svg>
                          </div>
                          <div className="flex flex-1 justify-start">
                            <div className="flex flex-col space-y-1">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-9 h-9 mx-auto mb-4"
                                viewBox="0 0 448 512">
                                <path
                                  fill={`${presets.backgroundColorTopBar}`}
                                  d="M148 288h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm108-12v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 96v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm192 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96-260v352c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V112c0-26.5 21.5-48 48-48h48V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h128V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h48c26.5 0 48 21.5 48 48zm-48 346V160H48v298c0 3.3 2.7 6 6 6h340c3.3 0 6-2.7 6-6z"></path>
                              </svg>
                              <p
                                className="text-okkami-gray font-normal text-center type-regular medium uppercase"
                                style={{ color: presets.subtitleColor }}>
                                {t("CHECK_OUT")}
                              </p>
                              <p
                                style={{ color: presets.titleColor }}
                                className="text-okkami-dark-gray text-center type-title normal uppercase">
                                {booking.checkOutDateText}
                              </p>
                              <p
                                className="text-center type-normal normal"
                                style={{ color: presets.titleColor }}>
                                {presets.amPm
                                  ? convertToAmPm(
                                      configuration.checkOutTimeText
                                    )
                                  : configuration.checkOutTimeText}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="pt-5 mb-24 relative">
                        <FloatInput
                          id="adultFirstName"
                          name="adultFirstName"
                          label={t("FIRSTNAME")}
                          span={`*${t("REQUIRED")}`}
                          classNameLabel="mb-6 type-title small"
                          classNameSpan="text-gray-400 type-regular small pl-2"
                          classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                          onChange={handleFormChange}
                          value={formData.adultFirstName}
                          labelColor={presets.backgroundColorTopBar}
                          borderColor={presets.backgroundColorTopBar}
                        />
                        {!validateForm &&
                          submitted &&
                          isEmpty(formData.adultFirstName) && (
                            <p className="text-14 text-red-500 mt-5">
                              {t(
                                "PLEASE_FILL_OUT_REQUIRED",
                                "Please fill all the required fields"
                              )}
                            </p>
                          )}
                      </div>
                      <div className="mb-24 relative">
                        <FloatInput
                          id="adultLastName"
                          name="adultLastName"
                          label={t("LAST_NAME")}
                          span={`*${t("REQUIRED")}`}
                          classNameLabel="mb-6 type-title small"
                          classNameSpan="text-gray-400 type-regular small pl-2"
                          classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                          onChange={handleFormChange}
                          value={formData.adultLastName}
                          labelColor={presets.backgroundColorTopBar}
                          borderColor={presets.backgroundColorTopBar}
                        />
                        {!validateForm &&
                          submitted &&
                          isEmpty(formData.adultLastName) && (
                            <p className="text-14 text-red-500 mt-5">
                              {t(
                                "PLEASE_FILL_OUT_REQUIRED",
                                "Please fill all the required fields"
                              )}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                  style={{
                    backgroundColor: presets.backgroundColorTopBar,
                    color: presets.appHomeTextCallToAction,
                  }}
                  onClick={() => addBtn()}>
                  {t("ADD_YOURSELF_TO_THE_TRIP")}
                </button>
              </div>
            </section>
            <button
              className="app-button-mobile type-title medium "
              style={{
                backgroundColor: presets.backgroundColorTopBar,
                color: presets.appHomeTextCallToAction,
                position: "fixed",
                bottom: 0,
                left: 0,
              }}
              onClick={() => addBtn()}>
              {t("ADD_YOURSELF_TO_THE_TRIP")}
            </button>
            {loading && (
              <div className="fixed inset-0 flex justify-center items-center bg-gray-500 opacity-50 z-50">
                <ClipLoaderIcon
                  loadingState={loading}
                  size={30}
                  color={presets.backgroundColorTopBar}
                />
              </div>
            )}
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};

export default withTranslation()(AddYourselfToTrip);
